import React, { useState, useCallback } from "react";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Stack from "react-bootstrap/Stack";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import axios from "axios";
import useCookie from "../../hooks/useCookie";
import useCurrentUser from "../../hooks/useCurrentUser";
import { FileUploader } from "react-drag-drop-files";
import "./Generator.css";
import { ReactSketchCanvas } from 'react-sketch-canvas';
import { useRef } from "react";

// Component for inpainting

// Inpainting refers to the process by which an image, a mask, and
// a prompt are supplied to Stable Diffusion, and the output is the
// image which has been "edited" within the bounds of the mask to
// follow the supplied prompt.

async function addPromptToClass(prompt, user, csrfToken) {
  let classID = null;
  prompt = user.first_name + ": " + prompt;
  if (!user.is_staff) {
    await axios
      .get(`/api/students/${user.id}/`, {
        withCredentials: true,
      })
      .then((response) => {
        classID = response.data.classId;
      });
  } else {
    return;
  }
  let url = `/api/classes/${classID}/prompts/`;
  try {
    let response = await axios.post(
      url,
      { prompt },
      {
        headers: {
          "X-CSRFToken": csrfToken,
        },
      }
    );

    if (response.status !== 200) {
      throw new Error(
        `Failed to add prompt to class. Status code: ${response.status}`
      );
    }
    return response.data;
  } catch (error) {
    console.error("Error adding prompt to class:", error);
    throw error;
  }
}

/**
 * Retrieves the image from the Stable Diffusion API
 * @param {*} prompt
 * @param {*} advanced
 * @param {*} negative_prompt
 * @param {*} seed
 * @param {*} guidance
 * @param {*} steps
 * @returns
 */
async function fetchImage(prompt, image, mask) {
  const url = "http://152.14.85.26:80/";
  let img = "";
  try {
    await axios
      .post(url + "generateinpainting", { prompt: prompt, image: image, mask: mask })
      .then((res) => {
        const data = res.data;
        img = "data:image/png;base64," + data;
      });
  } catch (error) {
    // TODO: Add error handling
  }
  return img;
}

/**
 * Adds the generated image to the user's gallery
 * @param {*} user
 * @param {*} csrftoken
 * @param {*} prompt
 * @param {*} data
 */
async function addImageToGallery(user, csrftoken, prompt, data) {
  let recent = null;
  let newImage = null;

  if (!user.is_staff) {
    await axios
      .get(`/api/students/${user.id}/`, {
        withCredentials: true,
      })
      .then((response) => {
        recent = response.data.recent;
        newImage = {
          prompt: prompt,
          picture: data,
          gallery: `/api/galleries/${recent}/`,
        };
      });
  } else {
    await axios
      .get(`/api/teachers/${user.id}/`, {
        withCredentials: true,
      })
      .then((response) => {
        recent = response.data.recent;
        newImage = {
          prompt: prompt,
          picture: data,
          gallery: `/api/galleries/${recent}/`,
        };
      });
  }

  const headers = {
    "X-CSRFToken": csrftoken,
  };

  await axios
    .post(`/api/images/`, newImage, { headers })
    .then((response) => {
      // TODO: Do something
    })
    .catch((error) => {
      // TODO: Add error handling
    });
}

/**
 * Main component for the Generator page
 *
 * @param {*} props
 * @returns
 */
function InPainting(props) {
  const [imgIsShown, setImgIsShown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [masking, setMasking] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [givenImg, setGivenImg] = useState("");
  const [bias, setBias] = useState(false);
  const csrftoken = useCookie("csrftoken");
  const user = useCurrentUser();

  const canvasRef = useRef(null);

  const handleClick = useCallback(async () => {
    let prompt = document.getElementById("prompt").value;
    if (bias) {
      prompt = prompt + " diverse";
    }

    setLoading(true);
    // setImgIsShown(false);
    setImageUrl(null);

    try {
      const mask = canvasRef.current._canvas.toDataURL().split(",")[1];
      const img = await fetchImage(prompt, givenImg, mask); //mask
      await addImageToGallery(user, csrftoken, prompt, img.split(",")[1]);
      // Not relevant to current build
      // If we continue to use /prompts/, make sure that
      // correct CSRF token is passed with request
      // await addPromptToClass(prompt, user, csrftoken);
      setLoading(false);
      setImgIsShown(true);
      setImageUrl(img);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }, [bias, csrftoken, user, givenImg]);

  const handleImageUpload = (file) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const givenImg = reader.result.split(",")[1];
      setGivenImg(givenImg);
    };
    setMasking(true);
  };

  return (
    <div id="generation-area">
      <div style={{ width: "100%" }}>
        <p>Draw on top of the generated image with your cursor!</p>
        <Stack direction="horizontal" gap={3}>
          Prompt:
          <Form.Control
            id="prompt"
            className="me-auto"
            placeholder="Enter your prompt here..."
          />
          <Button
            className="text-white"
            variant="info"
            onClick={() => handleClick()}
          >
            Generate
          </Button>
        </Stack>
      </div>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <div className="blob-2" id="spinner">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : imgIsShown ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <img
            src={imageUrl}
            alt="generated"
            style={{ width: "512px", height: "512px" }}
          />
        </div>
      ) : masking ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <ReactSketchCanvas
            ref={(canvas) => (canvasRef.current = canvas)}
            width="100%"
            height="100%"
            style={{
              border: "0.0625rem solid #fff"
            }}
            strokeWidth={20}
            strokeColor="black"
            backgroundImage={`data:image/png;base64,${givenImg}`}
            preserveBackgroundImageAspectRatio="xMidYMid meet"
          />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <FileUploader
            handleChange={handleImageUpload}
            name="file"
            types={["PNG", "JPEG"]}
          />
        </div>
      )}
      <div id="bias-toggle">
        <Form.Check
          type="switch"
          label="Bias Correction"
          onClick={() => setBias(!bias)}
          style={{ marginRight: "0.5vw" }}
        />
        <span>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="tooltip-disabled">
                Switch for adding bias correction to create more diverse
                images
              </Tooltip>
            }
          >
            <span style={{ marginRight: "1vw" }}>ⓘ</span>
          </OverlayTrigger>
        </span>
      </div>
    </div>
  );
}

export default InPainting;